import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Assignee from 'models/assignee';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemMenuDisplayer from 'components/customer/conversation_history/conversation_items_v2/menu/conversation_item_menu_displayer';
import ConversationItemType from 'models/conversation_item_type';
import { createConversationItemFromItemLink } from 'models/item_link_content';
import { Device, getDevice } from 'scripts/lib/device_detector';
import ErrorBoundary from 'components/common/utilities/error_boundary';
import { findTaskThreadReplyComposition } from 'actions/composition/lib/composer_compositions';
import { getIsCommentingEnabledForTask } from 'scripts/application/selectors/customer';
import Linkifier from 'components/lib/linkifier';
import LongDateTime from 'components/lib/date/long_date_time';
import NavigateToItem from 'actions/conversation_item/navigate_to_item';
import SafeHtml from 'components/common/safe_html';
import TaskAnalyticsLocations from 'actions/conversation_item/task/task_analytics_locations';
import TaskCommentEditor from './task_comment_editor';
import TaskCommentEditorNew from './task_comment_editor_new';
import TaskHeader from 'components/customer/conversation_history/conversation_items_v2/task/task_header';
import TaskThread from './task_thread';
import TaskThreadReplyComposer from './task_thread_reply_composer';
import TaskTopicsPills from 'components/customer/conversation_history/conversation_items_v2/task/task_topics_pills';
import TrackCommentPanelVisibility from 'actions/task/track_comment_panel_visibility';
import { typeReflect } from 'models/conversation_item/content_from_js';
import UpdateTaskAssignee from 'actions/conversation_item/task/update_task_assignee';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import { useOnUnmount } from 'components/hooks/lifecycle_hooks';

export function TaskCommentPanel({
  canRedactComment,
  canSendResponse,
  hasReplyDraft,
  isThreadLoading,
  replyFrom,
  replyCc,
  replyTo,
  replySubject,
  thread = [],
  currentAgentId,
  isCommentingEnabled,
  hasConversationGeneral,
  isTopicsOnTasksEnabled,
  isSlateBetaEnabled,
  item,
  lastOpenedAt,
  selectedComment,
}) {
  const { t } = useTranslation();
  const hasThreadEmails = thread.some(entry => entry.kind === 'email');
  const executeAction = useExecuteAction();
  const itemId = item?.id;
  const [messagesRef, messagesContentRef] = useScrollToBottom(itemId, thread.length, selectedComment);

  const [composerMode, setComposerMode] = useComposerMode(itemId, hasReplyDraft);

  const onChangeAssignee = useCallback(
    ({ agentId, routingGroupId }) =>
      executeAction(UpdateTaskAssignee, {
        assignee: new Assignee({ agentId: agentId || '', routingGroupId }),
        conversationItemId: itemId,
        location: TaskAnalyticsLocations.SIDE_PANEL,
      }),
    [executeAction, itemId]
  );

  // we want to log the comment panel as OPEN every time it becomes visible
  // or displays a different task (this can happen while it is already open)
  // BUT we only want to log the panel as CLOSED when it closes completely and
  // NOT when it refreshes to show a new task
  let itemIdRef = useRef(itemId);
  useEffect(() => {
    executeAction(TrackCommentPanelVisibility, { itemId, isVisible: true });
    itemIdRef.current = itemId;
  }, [executeAction, itemId]);

  useOnUnmount(() => {
    executeAction(TrackCommentPanelVisibility, { itemId: itemIdRef.current, isVisible: false });
  }, []);

  if (!item) {
    return null;
  }
  const isLink = item.isLink();
  const task = item.content;

  return (
    <Panel data-aid="taskCommentPanel" tabIndex="0">
      {renderTaskDetail()}
      <StyledTaskMessages $currentAgentId={currentAgentId} ref={messagesRef}>
        <div ref={messagesContentRef}>
          <TaskContent item={item} />
          {renderThread()}
        </div>
      </StyledTaskMessages>
      {renderComposer()}
    </Panel>
  );

  function renderTaskDetail() {
    return (
      <StyledDetail>
        <TaskHeader
          hasConversationGeneral={hasConversationGeneral}
          isLink={isLink}
          item={item}
          location={TaskAnalyticsLocations.SIDE_PANEL}
          onChangeAssignee={onChangeAssignee}
        />
        <StyledItemMenu hideComment item={item} location={TaskAnalyticsLocations.SIDE_PANEL} shouldShowOverflowMenu />
        <StyledDueDate>
          {t('Due')}
          <LongDateTime className="taskItem-dueDate-timestamp" timestamp={moment(task.dueAt)} />
        </StyledDueDate>
        {renderTopics()}
      </StyledDetail>
    );
  }

  function renderThread() {
    return <TaskThread canRedactComment={canRedactComment} selectedComment={selectedComment} thread={thread} />;
  }

  function renderTopics() {
    if (!hasConversationGeneral || !isTopicsOnTasksEnabled) {
      return null;
    }
    return (
      <StyledTopics>
        <ErrorBoundary errorMeta={{ taskId: item.id }} renderError={() => null}>
          <TaskTopicsPills taskId={item.id} />
        </ErrorBoundary>
      </StyledTopics>
    );
  }

  // An agent without conversation:general can still start a side conversation, so the response
  // editor stands alone when there is nothing to comment with.
  function renderComposer() {
    if (!isCommentingEnabled && !canSendResponse) return null;

    const withTabs = isCommentingEnabled && canSendResponse;
    const emailActive = canSendResponse && (!isCommentingEnabled || composerMode === 'email');

    // The comment editor stays mounted while hidden so switching tabs keeps what the agent typed;
    // the reply is a persisted composition, so its composer can come and go.
    return (
      <StyledComposer $withTabs={withTabs}>
        {withTabs && renderModeToggle()}
        {isCommentingEnabled && (
          <StyledComposerPane $hidden={emailActive} $underTabs={withTabs}>
            {renderCommentEditor()}
          </StyledComposerPane>
        )}
        {emailActive && (
          <StyledComposerPane $underTabs={withTabs}>
            <TaskThreadReplyComposer
              cc={replyCc}
              conversationId={item.conversationId}
              from={replyFrom}
              hasThreadEmails={hasThreadEmails}
              isThreadLoading={isThreadLoading}
              key={itemId}
              onCancel={withTabs ? () => setComposerMode('comment') : undefined}
              subject={replySubject}
              taskDueAt={item.content.dueAt}
              taskId={itemId}
              taskRoutingGroupId={item.content.assignee?.routingGroupId}
              to={replyTo}
            />
          </StyledComposerPane>
        )}
      </StyledComposer>
    );
  }

  function renderModeToggle() {
    return (
      <StyledModeToggle data-aid="taskComposerModeToggle">
        <StyledModeButton
          $active={composerMode === 'comment'}
          data-aid="taskComposerMode-comment"
          onClick={() => setComposerMode('comment')}
          type="button"
        >
          {t('Comment')}
        </StyledModeButton>
        <StyledModeButton
          $active={composerMode === 'email'}
          data-aid="taskComposerMode-response"
          onClick={() => setComposerMode('email')}
          type="button"
        >
          {hasThreadEmails ? t('Send response') : t('Send email')}
        </StyledModeButton>
      </StyledModeToggle>
    );
  }

  function renderCommentEditor() {
    return isSlateBetaEnabled ? (
      <TaskCommentEditorNew itemId={itemId} lastOpenedAt={lastOpenedAt} />
    ) : (
      <TaskCommentEditor itemId={itemId} lastOpenedAt={lastOpenedAt} />
    );
  }
}

const Panel = styled.div`
  box-shadow: ${p => p.theme.boxShadow.small};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  outline: none;
  width: 100%;
  z-index: 3;
`;

// A task opens on comments, or on its reply when that has a saved draft. The reply stays open once its
// draft is sent or emptied: effects run in order, so a task switch resets before the draft check.
export function useComposerMode(itemId, hasReplyDraft) {
  const [composerMode, setComposerMode] = useState(hasReplyDraft ? 'email' : 'comment');
  useEffect(() => setComposerMode('comment'), [itemId]);
  useEffect(() => {
    if (hasReplyDraft) setComposerMode('email');
  }, [itemId, hasReplyDraft]);
  return [composerMode, setComposerMode];
}

function useScrollToBottom(itemId, numComments, selectedComment) {
  const ref = useRef(null);
  const contentRef = useRef(null);
  const isPinnedRef = useRef(false);

  // Only scroll to bottom when we mount or when someone else adds a comment, but don't do this if we
  // have a currently selected comment, as we'll want to scroll to that instead.
  useEffect(() => {
    if (numComments && !selectedComment) {
      scrollToBottom(ref.current);
      isPinnedRef.current = true;
    }
  }, [numComments]);

  // The thread keeps growing after that scroll: a clamped email gains its "Show more" link a render
  // later, and images load. Follow it down unless the agent has scrolled away from the bottom.
  useEffect(() => {
    const node = ref.current;
    const content = contentRef.current;
    if (!node || !content || typeof ResizeObserver === 'undefined') return undefined;

    const onScroll = () => {
      isPinnedRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 1;
    };
    const observer = new ResizeObserver(() => isPinnedRef.current && scrollToBottom(node));
    observer.observe(content);
    node.addEventListener('scroll', onScroll);
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', onScroll);
    };
  }, [itemId]);

  return [ref, contentRef];
}

function scrollToBottom(node) {
  const maxScrollTop = node.scrollHeight - node.clientHeight;
  node.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
}

TaskCommentPanel.propTypes = {
  canRedactComment: PropTypes.bool,
  canSendResponse: PropTypes.bool,
  hasReplyDraft: PropTypes.bool,
  isThreadLoading: PropTypes.bool,
  replyCc: PropTypes.arrayOf(PropTypes.string),
  replyFrom: PropTypes.string,
  replyTo: PropTypes.arrayOf(PropTypes.string),
  replySubject: PropTypes.string,
  thread: PropTypes.array,
  isTopicsOnTasksEnabled: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem),
};

function TaskContent({ item }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const { content } = item;
  const { attachments, bodyHtml, note } = content;

  const contentRef = useRef(null);

  if (bodyHtml) {
    return (
      <ContentWrapper data-aid="taskCommentPanel-taskMessageWrapper">
        <bdi>
          <SafeHtml data-aid="taskCommentPanel-taskMessage" html={bodyHtml} ref={contentRef} setDirection />
        </bdi>
        <Footer>
          <TimelineLink
            data-aid="taskCommentPanel-viewInTimeline"
            onClick={() => executeAction(NavigateToItem, { itemId: item.id })}
          >
            {t('View in timeline')}
          </TimelineLink>
          {attachments && attachments.length ? (
            <NumAttachments>{t('{{count}} attachments', { count: attachments.length })}</NumAttachments>
          ) : null}
        </Footer>
      </ContentWrapper>
    );
  } else if (note) {
    return (
      <ContentWrapper data-aid="taskCommentPanel-taskMessageWrapper">
        <Linkifier data-aid="taskCommentPanel-taskMessage">{note}</Linkifier>
        <Footer>
          <TimelineLink
            data-aid="taskCommentPanel-viewInTimeline"
            onClick={() => executeAction(NavigateToItem, { itemId: item.id })}
          >
            {t('View in timeline')}
          </TimelineLink>
          {attachments && attachments.length ? (
            <NumAttachments>{t('{{count}} attachments', { count: attachments.length })}</NumAttachments>
          ) : null}
        </Footer>
      </ContentWrapper>
    );
  }

  return null;
}

const ContentWrapper = styled.div`
  // Surface token (light in light theme, dark-gray in dark theme) so the box-shadow reads as a raised
  // card in both themes; without a fill it blends into the column background in dark mode.
  background-color: ${p => p.theme.colors.white};
  box-shadow: ${p => p.theme.boxShadow.small};
  border-radius: 8px;
  margin-bottom: 16px;
  margin-left: 8px;
  margin-right: 8px;
  padding: 8px;
`;

const NumAttachments = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: var(--font-size-reduced);
`;

const TimelineLink = styled.a`
  cursor: pointer;
  font-size: var(--font-size-reduced);

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;

const StyledItemMenu = styled(ConversationItemMenuDisplayer)`
  .conversationItemMenu-container {
    top: 25px;
    .conversationItemMenu-button {
      padding-top: 0;
    }
  }
`;

// The thread's email cards share this gray, so a rule keeps the header from running into them.
const StyledDetail = styled.div`
  background: ${p => p.theme.colors.gray100};
  border-bottom: 1px solid ${p => p.theme.colors.gray200};
  padding: 20px 16px 16px;
`;

const StyledDueDate = styled.div`
  font-weight: 600;
`;

const StyledComposer = styled.div`
  flex: none;
  ${p => p.$withTabs && `border-top: 1px solid ${p.theme.colors.gray200}; margin-top: 12px;`}
`;

const StyledComposerPane = styled.div`
  ${p => p.$hidden && 'display: none;'}
  ${p =>
    p.$underTabs &&
    `[data-aid='taskCommentEditor-editor'], [data-aid='taskEmailEditor'] { border-top: none; margin-top: 8px; }`}
`;

const StyledTopics = styled.div`
  margin-top: 12px;

  /* Left-align the menu opener (and its search input) with the Due date and
     task header, overriding the shared opener's horizontal padding which is
     meant for the bottom response bar. */
  .conversationTopic-menuOpener {
    padding-left: 0;
  }
`;

const StyledTaskMessages = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 0 0 0;

  agentmention {
    background-color: ${p => p.theme.colors.gray100};
    border-radius: 4px;
    color: ${p => p.theme.colors.green400};
    font-weight: bold;
    padding: 4px;

    &:hover {
      background-color: ${p => p.theme.colors.gray200};
    }

    &[data-agentid="${p => p.$currentAgentId}"], &[data-agentid="${p => p.$currentAgentId}"]:hover {
      background-color: ${p => p.theme.colors.yellow200};
    }
`;

const StyledModeToggle = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.gray200};
  display: flex;
  gap: 4px;
  margin: 0 16px;
  padding-top: 4px;
`;

const StyledModeButton = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${p => (p.$active ? p.theme.colors.green400 : 'transparent')};
  color: ${p => (p.$active ? p.theme.colors.green400 : p.theme.colors.gray600)};
  cursor: pointer;
  font-weight: ${p => (p.$active ? 'bold' : 'normal')};
  margin-bottom: -1px;
  padding: 4px 4px 6px;
`;

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const agentPreferencesProvider = getProvider('agentPreferences');
  const commentPanelProvider = getProvider('commentPanel');
  const currentAgentProvider = getProvider('currentAgent');
  const historyProvider = getProvider('conversationHistory');
  const taskInfoProvider = getProvider('taskInfo');

  const sidePanelProvider = getProvider('sidePanel');

  const panel = commentPanelProvider.get();
  const selectedItemId = panel.selectedItemId;
  const lastOpenedAt = sidePanelProvider.get().panelLastShownAt;

  let item = historyProvider.findBy({ id: selectedItemId });
  item = item && historyProvider.isPending(selectedItemId) ? historyProvider.getPending(selectedItemId) : item;
  if (!item) return {};

  const isLinkedItem = typeReflect.instanceToType(item.content) === ConversationItemType.ITEM_LINK;
  if (isLinkedItem) {
    item = createConversationItemFromItemLink(item);
  }

  let taskInfo = taskInfoProvider.findBy({ id: item.id });
  if (taskInfo && taskInfoProvider.isPending(item.id)) {
    taskInfo = taskInfoProvider.getPending(item.id);
  }
  let thread = taskInfo?.getThread() || [];
  const preferences = agentPreferencesProvider.get();

  const {
    from: replyFrom,
    to: replyTo,
    cc: replyCc,
    subject: replySubject,
  } = taskInfo ? taskInfo.replyDefaults() : { from: '', to: [], cc: [], subject: '' };

  const hasConversationGeneral = isFeatureEnabled('conversation:general');

  return {
    canRedactComment: isFeatureEnabled('complianceManagement'),
    canSendResponse: canSendTaskEmail({ getProvider, isFeatureEnabled }, item, isLinkedItem),
    hasReplyDraft: hasReplyContent(findTaskThreadReplyComposition(getProvider('compositions'), item.id)),
    isThreadLoading: !!taskInfo && !!taskInfo.areEmailsLoading,
    replyFrom,
    replyTo,
    replyCc,
    replySubject,
    thread,
    isCommentingEnabled:
      hasConversationGeneral &&
      getIsCommentingEnabledForTask(
        { taskInfo: taskInfoProvider, conversationHistory: historyProvider },
        { taskId: item.id }
      ),
    hasConversationGeneral,
    isTopicsOnTasksEnabled: isFeatureEnabled('topicsOnTasks'),
    currentAgentId: currentAgentProvider.get().id,
    // Force the newer Slate editor on mobile: the legacy Slate 0.44 editor handles soft (esp. Android)
    // keyboards poorly. Otherwise respect the feature flag + agent preference. Mirrors composition.jsx.
    isSlateBetaEnabled:
      getDevice() !== Device.Desktop || (preferences.experimentalEditorEnabled && isFeatureEnabled('slateBeta')),
    item,
    lastOpenedAt,
    selectedComment: panel.selectedComment,
  };
}

const TaskCommentPanelContainer = connect(mapStateToProps)(TaskCommentPanel);
export default TaskCommentPanelContainer;

// The reply composer creates its draft on mount, so an existing draft alone doesn't mean the agent wrote one.
function hasReplyContent(composition) {
  return !!composition && !composition.isEmpty();
}

// Any open task can email a third party; its first email starts the thread. A task linked from another
// customer is left out: its link id isn't a task the thread endpoint accepts.
function canSendTaskEmail({ getProvider, isFeatureEnabled }, item, isLinkedItem) {
  const channelConfiguration = getProvider('channelConfiguration').get();
  const hasFromAddresses = !!channelConfiguration && channelConfiguration.fromAddresses().length > 0;
  return (
    !isLinkedItem &&
    !item.content.isClosed() &&
    hasFromAddresses &&
    isFeatureEnabled('taskSideThreads') &&
    isFeatureEnabled('conversation:outbound')
  );
}
